/**
 * Maintenance: SMS template id + message.
 * SMS is sent only when both fields are filled.
 * Admins can add custom templates (used in code via send_templated_sms(key, ...)).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { MessageSquare, RefreshCw, Pencil, Plus, Trash2 } from 'lucide-react';
import { dealerService } from '../services/dealer.service';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useDialog } from '../contexts/DialogContext';

function extractPlaceholders(body) {
  const seen = [];
  const re = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
  let match;
  while ((match = re.exec(body || '')) !== null) {
    if (!seen.includes(match[1])) seen.push(match[1]);
  }
  return seen;
}

function TemplateModal({ isOpen, onClose, onSave, row, mode = 'edit' }) {
  const isCreate = mode === 'create';
  const [form, setForm] = useState({
    key: '',
    name: '',
    template_id: '',
    body: '',
    variables: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm({
      key: row?.key || '',
      name: row?.name || '',
      template_id: row?.template_id || '',
      body: row?.body || '',
      variables: Array.isArray(row?.variables) ? row.variables.join(', ') : '',
    });
    setError('');
  }, [row, isCreate, isOpen]);

  const detectedVars = useMemo(() => extractPlaceholders(form.body), [form.body]);
  const displayVars = form.variables
    ? form.variables
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
    : detectedVars;

  if (!isOpen) return null;
  if (!isCreate && !row) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const variables = form.variables
        ? form.variables
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : detectedVars;

      if (isCreate) {
        await onSave('create', {
          key: form.key.trim().toLowerCase(),
          name: form.name.trim(),
          template_id: form.template_id.trim(),
          body: form.body.trim(),
          variables,
        });
      } else {
        await onSave(row.key, {
          template_id: form.template_id.trim(),
          body: form.body.trim(),
          name: row.custom ? form.name.trim() : undefined,
          variables: row.custom ? variables : undefined,
        });
      }
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.detail || err?.message;
      setError(typeof detail === 'string' ? detail : 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b">
          <h2 className="text-lg font-semibold">
            {isCreate ? 'Add SMS template' : `Edit ${row.name || row.key}`}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            SMS is sent only when both template ID and message are filled. Use placeholders like{' '}
            <code className="text-xs">{`{name}`}</code> in the message; code passes values by key.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {isCreate && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template key</label>
                <input
                  value={form.key}
                  onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 text-sm font-mono"
                  placeholder="payment_reminder"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lowercase, underscores. Used in code: send_templated_sms(&apos;your_key&apos;, ...)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  placeholder="Payment reminder"
                  required
                />
              </div>
            </>
          )}
          {!isCreate && row?.custom && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm"
                required
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template ID</label>
            <input
              value={form.template_id}
              onChange={(e) => setForm((f) => ({ ...f, template_id: e.target.value }))}
              className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="MSG91 / DLT template id"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template message</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              rows={5}
              className="w-full border rounded-md px-3 py-2 text-sm font-mono"
            />
            {displayVars.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Placeholders: {displayVars.map((v) => `{${v}}`).join(', ')}
              </p>
            )}
          </div>
          {(isCreate || row?.custom) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Variables (optional)
              </label>
              <input
                value={form.variables}
                onChange={(e) => setForm((f) => ({ ...f, variables: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm"
                placeholder="name, amount, svc"
              />
              <p className="text-xs text-gray-500 mt-1">
                Comma-separated. Leave blank to auto-detect from message placeholders.
              </p>
            </div>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-md text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isCreate ? 'Add template' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const SmsTemplates = () => {
  const { toast, confirm } = useDialog();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const data = await dealerService.getSmsTemplates();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading SMS templates:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const handleSave = async (keyOrMode, payload) => {
    if (keyOrMode === 'create') {
      await dealerService.createSmsTemplate(payload);
    } else {
      await dealerService.updateSmsTemplate(keyOrMode, payload);
    }
    fetchRows();
  };

  const handleDelete = async (row) => {
    if (!row?.custom) return;
    const ok = await confirm({
      title: 'Delete template',
      message: `Delete template "${row.name}" (${row.key})? This cannot be undone.`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await dealerService.deleteSmsTemplate(row.key);
      fetchRows();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.detail || err?.message;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete template.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white flex justify-between items-center rounded-lg bxshadowhd mrgall py-3 padlr15 border">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-6 h-6" /> SMS Templates
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" /> Add template
          </button>
          <button
            onClick={fetchRows}
            className="flex items-center gap-1 px-3 py-2 border rounded-md text-sm hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg bxshadowhd p-4 mrgall border text-sm text-gray-600">
        Built-in templates are used by the application (OTP, booking confirmed). Add custom templates
        here with a unique key, then call <code className="text-xs bg-gray-100 px-1">send_templated_sms(&apos;your_key&apos;, mobile, variables)</code> from server code.
      </div>

      <div className="bg-white rounded-lg bxshadowhd p-2 mrgall border">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Name', 'Template ID', 'Message', 'Type', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="px-3 py-3 text-left font-medium text-gray-700">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-6 text-center text-gray-500">
                      No SMS templates found
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const ready = !!(r.template_id || '').trim() && !!(r.body || '').trim();
                    return (
                      <tr key={r.key} className="hover:bg-gray-50">
                        <td className="px-3 py-3">
                          <div className="font-medium text-gray-900">{r.name}</div>
                          <div className="text-xs text-gray-500 font-mono">{r.key}</div>
                        </td>
                        <td className="px-3 py-3 font-mono text-gray-800">
                          {(r.template_id || '').trim() || '—'}
                        </td>
                        <td className="px-3 py-3 text-gray-600 max-w-md">
                          <span className="line-clamp-2">{r.body || '—'}</span>
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={`px-2 py-1 text-xs rounded-md ${
                              r.custom
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {r.custom ? 'Custom' : 'Built-in'}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={`px-2 py-1 text-xs rounded-md ${
                              ready ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {ready ? 'Will send' : 'Won’t send'}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditing(r)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            {r.custom && (
                              <button
                                onClick={() => handleDelete(r)}
                                className="text-red-600 hover:text-red-800"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <TemplateModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        onSave={handleSave}
        row={null}
        mode="create"
      />
      <TemplateModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        onSave={handleSave}
        row={editing}
        mode="edit"
      />
    </div>
  );
};

export default SmsTemplates;
